import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ENROLL_URL } from "../../config";

const ext = ENROLL_URL.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {};

const links = [
  { label: "The Story", href: "#story" },
  { label: "The Journey", href: "#journey" },
  { label: "Community", href: "#community" },
  { label: "About", href: "#about" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-ivory/80 backdrop-blur-xl border-b border-ink/8" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 md:px-10">
        <a href="#top" data-testid="nav-logo" className="font-display text-lg tracking-tight text-ink md:text-xl">
          Creating Wealth
        </a>

        <nav className="hidden items-center gap-9 md:flex">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              className="group relative font-sans text-sm tracking-wide text-ink/70 transition-colors duration-300 hover:text-ink"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-terracotta transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <a
            href={ENROLL_URL}
            data-testid="nav-cta"
            {...ext}
            className="hidden bg-ink px-6 py-3 font-sans text-xs font-medium uppercase tracking-[0.16em] text-ivory transition-colors duration-300 hover:bg-terracotta md:inline-block"
          >
            Join Us
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className="flex h-9 w-9 flex-col items-center justify-center gap-1.5 md:hidden"
            aria-label="Menu"
          >
            <span className={`h-px w-6 bg-ink transition-transform ${open ? "translate-y-[3.5px] rotate-45" : ""}`} />
            <span className={`h-px w-6 bg-ink transition-transform ${open ? "-translate-y-[3.5px] -rotate-45" : ""}`} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-ink/8 bg-ivory/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col px-6 py-4">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`mobile-nav-link-${l.href.slice(1)}`}
                  onClick={() => setOpen(false)}
                  className="border-b border-ink/5 py-3 font-display text-lg text-ink"
                >
                  {l.label}
                </a>
              ))}
              <a href={ENROLL_URL} {...ext} data-testid="mobile-nav-cta" onClick={() => setOpen(false)} className="mt-4 bg-terracotta px-6 py-3 text-center font-sans text-xs font-medium uppercase tracking-[0.16em] text-white">
                Join Us
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
